import { useParams } from 'react-router';
import { Footer, Loading, TitleBar } from '../../../../components';
import GenericListManager from '../../../../components/generic/ListManager/GenericListManager';
import { FETCH_IT_APPLICATIONS } from '../../../dataInputs/queries/itApplications';

import { GET_QUALITATIVE_FACTOR } from '../../../dataInputs/data-inputs/qualitative-factors/queries';
import { useQuery } from '@apollo/client';
import { useState } from 'react';
import QueryErrorBoundary from '../../../../components/error-handling';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import {
  GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS,
  QualitativeFactor,
  SET_IT_APPLICATIONS_TO_QUALITATIVE_FACTORS,
} from '../../enterprise-applications';
import { FetchedLocation } from '../LocationLevelLanding';
import { GET_LOCATION_NAME } from '../../../../graphql/utils/queries';
import { useQualitativeFactorWorkarounds } from '../../pages/workarounds/qualitative-factors/hooks';
import { useRemoveITApplicationConfirmation } from '../business-functions-workarounds/hooks';
import { RemoveITApplicationModal } from '../../components/RemoveITApplicationModal';

type QualitativeFactorData = {
  getQualitativeFactor: QualitativeFactor;
};

const LocationQualitativeFactorsSelection = () => {
  const { locationId, factorId } = useParams();
  const [qualitativeFactorName, setQualitativeFactorName] =
    useState<string>('');

  const { loading, error } = useQuery<QualitativeFactorData>(
    GET_QUALITATIVE_FACTOR,
    {
      variables: {
        uuid: factorId,
      },
      onCompleted: (data) =>
        setQualitativeFactorName(data.getQualitativeFactor.name),
    },
  );

  const [locationName, setLocationName] = useState<string>('');

  const { loading: qfLocationLoading, error: qfLocationError } =
    useQuery<FetchedLocation>(GET_LOCATION_NAME, {
      variables: {
        uuid: locationId,
      },
      onCompleted: (data) =>
        setLocationName(data.findLocationByUuid.locationName),
    });

  const qualitativeFactorIds = factorId ? [factorId] : [];
  const { data: workarounds } = useQualitativeFactorWorkarounds(
    qualitativeFactorIds,
    locationId,
  );

  const {
    isModalOpen,
    affectedApplications,
    handleBeforeUpdate,
    handleConfirm,
    handleCancel,
  } = useRemoveITApplicationConfirmation({
    workarounds: workarounds ?? [],
  });

  console.log('workarounds', workarounds);
  let headerContent;

  if (loading || qfLocationLoading) {
    headerContent = <Loading />;
  } else {
    headerContent = (
      <TitleBar
        title="Location Qualitative Factor Mapping"
        subtitle={`${qualitativeFactorName} Application Tagging`}
        brief="Select which IT applications directly support the company's reputation. Note that some applications are pre-selected based on the selections that were made when the application was first added to MEO."
      />
    );
  }

  return (
    <>
      <TitleUpdater
        title={`${locationName}/${qualitativeFactorName}/IT App selection`}
      />
      <QueryErrorBoundary error={[error, qfLocationError]}>
        {headerContent}
      </QueryErrorBoundary>
      <GenericListManager
        addedTitle="IT Applications Added"
        availableTitle="IT Applications Available"
        fetchAllQuery={{
          query: FETCH_IT_APPLICATIONS,
          identifier: 'findAllItApplications',
        }}
        fetchSelectedQuery={{
          query: GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS,
          identifier: 'getQualitativeFactorsSelectedITApplications',
          variableName: '0.itApplications',
          variables: {
            data: [factorId],
          },
        }}
        itemKey="uuid"
        itemLabel="application"
        updateMutation={{
          mutation: SET_IT_APPLICATIONS_TO_QUALITATIVE_FACTORS,
          variableName: 'data.qualitativeFactors[0].itApplications',
          variableStructure: {
            data: {
              qualitativeFactors: [
                {
                  itApplications: {},
                  uuid: factorId,
                  locationUUID: locationId,
                },
              ],
            },
          },
          formatFunction: (item: string) => ({ uuid: item }),
        }}
        beforeUpdate={(items) => {
          return new Promise((resolve) => {
            handleBeforeUpdate(items, () => resolve(true));
          });
        }}
        refetchQueries={[
          {
            query: GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS,
            variables: { data: [factorId] },
          },
        ]}
        isLocation={!!locationName}
      />
      <RemoveITApplicationModal
        isOpen={isModalOpen}
        onCancel={handleCancel}
        onRemove={handleConfirm}
        applications={affectedApplications}
      />
      <Footer
        backPath={`/cyber/location-level/${locationId}/qualitative-factors`}
        nextPath={`/cyber/location-level/${locationId}/qualitative-factors/${factorId}/it-application-description`}
        goNextText="Add Descriptions"
      />
    </>
  );
};

export default LocationQualitativeFactorsSelection;
